import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQuery } from '@apollo/client';
import { Button, Progress, Space, Table, Tag, Typography } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
// Api
import { recruitmentClient } from 'api/Recruitment/client';
import { LIST_IMPORT_CSV_JOBS } from 'api/Recruitment/queries';
// Types
import {
  ListImportCsvJobs,
  ListImportCsvJobs_listImportCsvJobs_items,
  ListImportCsvJobsVariables,
} from 'api/Recruitment/types/ListImportCsvJobs';
// UI
import { errorNotification } from 'ui/Notification';
// Components
import ImportCsvModal from './components/ImportCsvModal/ImportCsvModal';
import JobDetailsDrawer from './components/JobDetailsDrawer/JobDetailsDrawer';
// Helpers
import {
  formatImportJobDate,
  formatLastRefreshedAt,
  getImportJobStatusColor,
} from './helpers';
// Styles
import styles from './ExternalTalentImport.module.scss';

const PAGE_SIZE = 25;
const POLL_INTERVAL_SECONDS = 15;
const { Text } = Typography;

const ExternalTalentImport = (): JSX.Element => {
  const [jobs, setJobs] = useState<ListImportCsvJobs_listImportCsvJobs_items[]>(
    []
  );
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [selectedJob, setSelectedJob] =
    useState<ListImportCsvJobs_listImportCsvJobs_items | null>(null);
  const [isImportModalVisible, setIsImportModalVisible] =
    useState<boolean>(false);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null);

  const nextRefreshAtRef = useRef<number | null>(null);
  const nextRefreshTextRef = useRef<HTMLSpanElement | null>(null);
  const { data, fetchMore, loading, refetch, startPolling, stopPolling } =
    useQuery<ListImportCsvJobs, ListImportCsvJobsVariables>(
      LIST_IMPORT_CSV_JOBS,
      {
        client: recruitmentClient,
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
        variables: { limit: PAGE_SIZE },
      }
    );

  const isRefreshButtonLoading = loading || isRefreshing;
  const hasProcessingJob = jobs.some((job) => job.status === 'PROCESSING');

  const updateNextRefreshText = useCallback(() => {
    if (!nextRefreshTextRef.current || !nextRefreshAtRef.current) return;

    const seconds = Math.max(
      0,
      Math.ceil((nextRefreshAtRef.current - Date.now()) / 1000)
    );

    nextRefreshTextRef.current.textContent = `Auto refresh in: ${seconds} ${
      seconds === 1 ? 'second' : 'seconds'
    }`;

    if (seconds === 0) {
      nextRefreshAtRef.current = Date.now() + POLL_INTERVAL_SECONDS * 1000;
    }
  }, []);

  const resetNextRefreshTimer = useCallback(
    (shouldRun: boolean) => {
      nextRefreshAtRef.current = shouldRun
        ? Date.now() + POLL_INTERVAL_SECONDS * 1000
        : null;
      updateNextRefreshText();
    },
    [updateNextRefreshText]
  );

  useEffect(() => {
    const response = data?.listImportCsvJobs;

    if (!response) return;

    const nextJobs = response.items || [];

    setJobs(nextJobs);
    setNextToken(response.nextToken || null);
    setLastRefreshedAt(new Date());
    resetNextRefreshTimer(nextJobs.some((job) => job.status === 'PROCESSING'));
  }, [data, resetNextRefreshTimer]);

  useEffect(() => {
    if (hasProcessingJob) {
      startPolling(POLL_INTERVAL_SECONDS * 1000);
    } else {
      stopPolling();
      resetNextRefreshTimer(false);
    }

    return () => stopPolling();
  }, [hasProcessingJob, resetNextRefreshTimer, startPolling, stopPolling]);

  useEffect(() => {
    if (!hasProcessingJob) return undefined;

    if (!nextRefreshAtRef.current) {
      resetNextRefreshTimer(true);
    }

    updateNextRefreshText();
    const interval = window.setInterval(updateNextRefreshText, 1000);

    return () => window.clearInterval(interval);
  }, [hasProcessingJob, resetNextRefreshTimer, updateNextRefreshText]);

  const handleImportModalOpen = useCallback(() => {
    setIsImportModalVisible(true);
  }, []);

  const handleImportModalClose = useCallback(() => {
    setIsImportModalVisible(false);
  }, []);

  const handleImportSuccess = useCallback(async () => {
    setIsImportModalVisible(false);
    await refetch({ limit: PAGE_SIZE });
  }, [refetch]);

  const handleRefresh = useCallback(async () => {
    try {
      setIsRefreshing(true);
      stopPolling();
      const result = await refetch({ limit: PAGE_SIZE });
      const refreshedJobs = result.data?.listImportCsvJobs?.items || [];

      setLastRefreshedAt(new Date());
      resetNextRefreshTimer(
        refreshedJobs.some((job) => job.status === 'PROCESSING')
      );
    } catch (error) {
      errorNotification('Import jobs could not be refreshed.');
    } finally {
      setIsRefreshing(false);

      if (hasProcessingJob) {
        startPolling(POLL_INTERVAL_SECONDS * 1000);
      }
    }
  }, [
    hasProcessingJob,
    refetch,
    resetNextRefreshTimer,
    startPolling,
    stopPolling,
  ]);

  const handleLoadMore = async () => {
    const loadMoreNextToken = nextToken;

    if (!loadMoreNextToken) return;

    try {
      setIsLoadingMore(true);
      stopPolling();

      await fetchMore({
        variables: { limit: PAGE_SIZE, nextToken: loadMoreNextToken },
      });
    } catch (error) {
      errorNotification('Import jobs could not be loaded.');
    } finally {
      setIsLoadingMore(false);

      if (hasProcessingJob) {
        startPolling(POLL_INTERVAL_SECONDS * 1000);
      }
    }
  };

  const columns = useMemo<
    ColumnsType<ListImportCsvJobs_listImportCsvJobs_items>
  >(
    () => [
      {
        title: 'Job ID',
        dataIndex: 'jobId',
        key: 'jobId',
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        render: (status: string) => (
          <Tag color={getImportJobStatusColor(status)}>{status}</Tag>
        ),
      },
      {
        title: 'Progress',
        dataIndex: 'progressPercent',
        key: 'progressPercent',
        render: (progressPercent: number | null) => (
          <Progress percent={progressPercent || 0} size="small" />
        ),
      },
      {
        title: 'Submitted At',
        dataIndex: 'createdAt',
        key: 'createdAt',
        render: formatImportJobDate,
      },
    ],
    []
  );

  return (
    <>
      <Space className={styles.toolbar} align="center">
        <Space align="center">
          <Button
            aria-label="Refresh import jobs"
            disabled={isRefreshButtonLoading}
            icon={<ReloadOutlined spin={isRefreshButtonLoading} />}
            onClick={handleRefresh}
            type="text"
          />
          {lastRefreshedAt && (
            <Space direction="vertical" size={0}>
              <Text type="secondary" className={styles.lastRefreshed}>
                Last refreshed: {formatLastRefreshedAt(lastRefreshedAt)}
              </Text>
              {hasProcessingJob && (
                <Text type="secondary" className={styles.lastRefreshed}>
                  <span ref={nextRefreshTextRef}>
                    Auto refresh in: {POLL_INTERVAL_SECONDS} seconds
                  </span>
                </Text>
              )}
            </Space>
          )}
        </Space>

        <Button type="primary" onClick={handleImportModalOpen}>
          Import
        </Button>
      </Space>

      <Table<ListImportCsvJobs_listImportCsvJobs_items>
        columns={columns}
        dataSource={jobs}
        loading={loading}
        pagination={false}
        rowKey="jobId"
        onRow={(job) => ({
          onClick: () => setSelectedJob(job),
        })}
        rowClassName={styles.row}
      />

      {nextToken && (
        <div className={styles.loadMoreWrapper}>
          <Button loading={isLoadingMore} onClick={handleLoadMore}>
            Load more
          </Button>
        </div>
      )}

      <ImportCsvModal
        isVisible={isImportModalVisible}
        onClose={handleImportModalClose}
        onSuccess={handleImportSuccess}
      />

      <JobDetailsDrawer
        job={selectedJob}
        onClose={() => setSelectedJob(null)}
      />
    </>
  );
};

export default ExternalTalentImport;
